import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";

/**
 * Super Admin → System Diagnostics.
 * Read-only observability surface. No secret values are ever displayed.
 * Backed by /api/admin/diagnostics/* endpoints.
 */
export default function SystemDiagnostics() {
  const { user, authHeader, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("health");
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "super_admin") { nav("/"); return; }
    refreshHealth();
  }, [loading, user?.id]);

  const refreshHealth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/diagnostics/health`, { headers: authHeader() });
      setHealth(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load health");
    }
  }, []);

  if (loading || !user || user.role !== "super_admin") return null;

  const dot = health?.overall === "healthy" ? "#16a34a" : health?.overall === "warning" ? "#eab308" : "#dc2626";
  const label = health?.overall === "healthy" ? "Healthy" : health?.overall === "warning" ? "Warning" : "Critical";

  return (
    <div className="App" data-testid="system-diagnostics">
      <Header hideSearch />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super Admin</div>
            <h1 className="font-display text-4xl mt-1" style={{ color: "var(--gf-text)" }}>System diagnostics</h1>
            <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
              Read-only overview. No API keys, webhook secrets, card data, or passwords are ever displayed.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-full border px-5 py-3" data-testid="health-pill"
               style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: dot }} />
            <span className="text-sm" style={{ color: "var(--gf-text)" }}>{label}</span>
            <button onClick={refreshHealth} className="text-xs underline" style={{ color: "var(--gf-text-muted)" }} data-testid="refresh-health-btn">Refresh</button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--gf-surface-2)" }}>
          {[
            ["health",        "Health"],
            ["stripe",        "Stripe"],
            ["webhooks",      "Webhooks"],
            ["subscription",  "Subscription lookup"],
            ["email",         "Email"],
            ["system",        "System"],
            ["logs",          "Logs"],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm rounded-t-lg ${tab === k ? "" : "opacity-60"}`}
              style={{
                background: tab === k ? "var(--gf-surface-2)" : "transparent",
                color: tab === k ? "var(--gf-accent)" : "var(--gf-text-muted)",
                borderBottom: tab === k ? "2px solid var(--gf-accent)" : "2px solid transparent",
              }}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "health" && <HealthPanel health={health} />}
          {tab === "stripe" && <StripePanel />}
          {tab === "webhooks" && <WebhooksPanel />}
          {tab === "subscription" && <SubscriptionPanel />}
          {tab === "email" && <EmailPanel />}
          {tab === "system" && <SystemPanel />}
          {tab === "logs" && <LogsPanel />}
        </div>
      </div>
      <Footer />
    </div>
  );
}

// ---------- Reusable UI bits ----------
function Card({ children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border p-5" style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
      {children}
    </div>
  );
}
function KV({ k, v, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm py-1">
      <span style={{ color: "var(--gf-text-muted)" }}>{k}</span>
      <span data-testid={testid} className="text-right font-mono text-xs break-all" style={{ color: "var(--gf-text)" }}>{v == null || v === "" ? "—" : String(v)}</span>
    </div>
  );
}
function OkPill({ ok, label }) {
  const bg = ok ? "#dcfce7" : "#fee2e2";
  const fg = ok ? "#166534" : "#991b1b";
  return (
    <span className="inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em]" style={{ background: bg, color: fg }}>
      {ok ? "OK" : "Fail"}{label ? ` · ${label}` : ""}
    </span>
  );
}

// ---------- Panels ----------
function HealthPanel({ health }) {
  if (!health) return <Card><p className="text-sm" style={{ color: "var(--gf-text-muted)" }}>Loading…</p></Card>;
  const c = health.checks || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Card testid="health-overall-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Overall</div>
        <div className="font-display text-3xl" style={{ color: "var(--gf-text)" }} data-testid="health-overall-value">{health.overall}</div>
        <KV k="Environment" v={health.app_env} />
        <KV k="Version" v={health.app_version} />
        <KV k="Server started at" v={health.server_started_at} />
        <KV k="Uptime (seconds)" v={health.uptime_seconds} />
      </Card>
      <Card testid="health-checks-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Checks</div>
        <div className="space-y-2">
          {Object.entries(c).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span style={{ color: "var(--gf-text)" }}>{k.replaceAll("_", " ")}</span>
              <OkPill ok={!!v.ok} label={v.error ? v.error.slice(0, 60) : (v.count != null ? String(v.count) : null)} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function StripePanel() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (refresh = false) => {
    try {
      const r = await axios.get(`${API}/admin/diagnostics/stripe${refresh ? "?refresh=true" : ""}`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Stripe status");
    }
  };
  const forceCheck = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostics/stripe/force-check`, {}, { headers: authHeader() });
      if (r.data.ok) {
        toast.success(`Stripe auth OK — key_mode=${r.data.key_mode} livemode=${r.data.livemode}`);
      } else {
        toast.error(`Stripe auth failed: ${r.data.error}`, { duration: 15000 });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force check failed", { duration: 10000 });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <Card><p className="text-sm">Loading…</p></Card>;

  const fpEnv = data.key_fingerprint_env || {};
  const fpSdk = data.key_fingerprint_sdk || {};
  const fpOs = data.key_fingerprint_env_live || {};
  const keyDrift =
    (fpEnv.prefix && fpSdk.prefix && (fpEnv.prefix !== fpSdk.prefix || fpEnv.suffix !== fpSdk.suffix)) ||
    (fpEnv.prefix && fpOs.prefix && (fpEnv.prefix !== fpOs.prefix || fpEnv.suffix !== fpOs.suffix));

  return (
    <div className="space-y-5">
      <Card testid="stripe-panel-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Stripe</div>
          <div className="flex items-center gap-2">
            <button data-testid="stripe-recheck-btn" onClick={() => load(true)} className="text-xs underline" style={{ color: "var(--gf-accent)" }}>Refresh cache</button>
            <button data-testid="stripe-force-check-btn" onClick={forceCheck} disabled={busy}
              className="text-xs rounded-full px-3 py-1 border disabled:opacity-50"
              style={{ borderColor: "var(--gf-surface)", color: "var(--gf-accent)" }}>
              {busy ? "Checking…" : "Run live check now"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <KV k="Mode" v={data.mode} testid="stripe-mode" />
            <KV k="Live mode" v={String(data.livemode)} testid="stripe-livemode" />
            <KV k="Auth OK" v={String(data.auth_ok)} testid="stripe-auth-ok" />
            <KV k="Last checked at" v={data.last_checked_at} />
            <KV k="Check source" v={data.check_source} />
            <KV k="Runtime SDK key matches env" v={String(data.runtime_key_matches_env)} testid="stripe-runtime-matches" />
            <KV k="Webhook secret configured" v={String(data.webhook_secret_configured)} testid="stripe-webhook-secret-ok" />
          </div>
          <div>
            <KV k="Monthly price ID" v={data.monthly_price_id} testid="stripe-monthly-price" />
            <KV k="Yearly first price ID" v={data.yearly_first_price_id} testid="stripe-yearly-first-price" />
            <KV k="Yearly renewal price ID" v={data.yearly_renewal_price_id} testid="stripe-yearly-renewal-price" />
            <KV k="Trial days" v={data.trial_days} />
          </div>
        </div>
      </Card>

      {data.auth_error && (
        <Card testid="stripe-error-card">
          <div className="text-sm uppercase tracking-[0.2em] mb-2" style={{ color: "#b91c1c" }}>Full Stripe auth exception (untruncated)</div>
          <KV k="Exception type" v={data.auth_error_type} testid="stripe-auth-error-type" />
          <KV k="HTTP status" v={data.auth_error_http_status} testid="stripe-auth-error-http" />
          <KV k="Stripe request id" v={data.auth_error_request_id} testid="stripe-auth-error-req-id" />
          <div className="mt-3 rounded-lg p-3 font-mono text-xs whitespace-pre-wrap break-all" style={{ background: "#fef2f2", color: "#7f1d1d" }} data-testid="stripe-auth-error-full">
            {data.auth_error}
          </div>
        </Card>
      )}

      <Card testid="stripe-fingerprint-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Loaded key fingerprint (never the full value)</div>
        {keyDrift && (
          <div className="mb-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#fef3c7", color: "#92400e" }} data-testid="stripe-key-drift-warning">
            ⚠ Key drift detected — the module-level STRIPE_API_KEY, the Stripe SDK&rsquo;s runtime key, and the current os.environ value are NOT all identical. Something else in the process is overriding the key.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ["Module STRIPE_API_KEY", fpEnv, "fp-env"],
            ["stripe_sdk.api_key (runtime)", fpSdk, "fp-sdk"],
            ["os.environ RIGHT NOW", fpOs, "fp-os"],
          ].map(([label, fp, tid]) => (
            <div key={tid} className="rounded-lg border p-3" style={{ borderColor: "var(--gf-surface)" }} data-testid={tid}>
              <div className="text-xs uppercase tracking-[0.15em] mb-2" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
              <KV k="Present" v={String(fp.present)} />
              <KV k="Mode" v={fp.mode} />
              <KV k="Length" v={fp.length} />
              <KV k="First 6" v={fp.prefix} />
              <KV k="Last 4" v={fp.suffix} />
              {fp.has_whitespace && <KV k="⚠ Whitespace" v={"true"} />}
              {fp.looks_publishable && <KV k="⚠ Publishable" v={"true (pk_ — wrong)"} />}
              {fp.looks_restricted && <KV k="⚠ Restricted" v={"true (rk_)"} />}
            </div>
          ))}
        </div>
      </Card>

      {(data.conflicting_env_vars || []).length > 0 && (
        <Card testid="stripe-conflicts-card">
          <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "#b91c1c" }}>Other Stripe env vars detected in the process</div>
          <p className="text-xs mb-3" style={{ color: "var(--gf-text-muted)" }}>
            These are set in the environment alongside STRIPE_API_KEY. If any of them are secret keys, they might be picked up by other libraries or config layers and shadow the intended key.
          </p>
          <div className="space-y-2">
            {data.conflicting_env_vars.map((c) => (
              <div key={c.env_var} className="text-xs font-mono flex flex-wrap items-center gap-3">
                <span style={{ color: "var(--gf-text)" }}>{c.env_var}</span>
                <span style={{ color: "var(--gf-text-muted)" }}>mode={c.mode}</span>
                <span style={{ color: "var(--gf-text-muted)" }}>{c.prefix}…{c.suffix}</span>
                <span style={{ color: "var(--gf-text-muted)" }}>len={c.length}</span>
                {c.is_secret_key && <span style={{ color: "#b91c1c" }}>⚠ secret key</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {(data.resolved_prices || []).length > 0 && (
        <Card testid="stripe-prices-card">
          <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Resolved Stripe prices</div>
          <p className="text-xs mb-3" style={{ color: "var(--gf-text-muted)" }}>
            Each configured Price ID resolved against Stripe. A red row means the actual amount or interval does NOT match what BookFreely expects for that tier — fix the env var to a Price ID with the correct value.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ color: "var(--gf-text-muted)" }}>
                  <th className="text-left py-2 pr-4">Tier</th>
                  <th className="text-left py-2 pr-4">Price ID</th>
                  <th className="text-left py-2 pr-4">Expected</th>
                  <th className="text-left py-2 pr-4">Actual on Stripe</th>
                  <th className="text-left py-2 pr-4">Product</th>
                  <th className="text-left py-2 pr-4">Live?</th>
                  <th className="text-left py-2 pr-4">Match</th>
                </tr>
              </thead>
              <tbody>
                {data.resolved_prices.map((p) => {
                  const ok = p.matches_expected === true;
                  const bad = p.matches_expected === false || !!p.error;
                  const rowColor = bad ? "#fef2f2" : (ok ? "transparent" : "transparent");
                  return (
                    <tr key={p.tier} data-testid={`price-row-${p.tier}`} className="border-t" style={{ borderColor: "var(--gf-surface)", background: rowColor }}>
                      <td className="py-2 pr-4 font-mono" style={{ color: "var(--gf-text)" }}>{p.tier}</td>
                      <td className="py-2 pr-4 font-mono text-[10px]" style={{ color: "var(--gf-text-muted)" }}>{p.price_id || "—"}</td>
                      <td className="py-2 pr-4">${(p.expected_amount || 0).toFixed(2)} / {p.expected_interval}</td>
                      <td className="py-2 pr-4" data-testid={`price-actual-${p.tier}`}>
                        {p.error ? <span style={{ color: "#b91c1c" }}>{p.error}</span> :
                          (p.actual_amount != null
                            ? `${(p.actual_currency || "usd").toUpperCase()} $${p.actual_amount.toFixed(2)} / ${p.actual_interval}`
                            : "—")}
                      </td>
                      <td className="py-2 pr-4">{p.product_name || "—"}</td>
                      <td className="py-2 pr-4">{p.livemode === null ? "—" : (p.livemode ? "live" : "test")}</td>
                      <td className="py-2 pr-4">
                        {p.matches_expected === true && <OkPill ok label="match" />}
                        {p.matches_expected === false && <OkPill ok={false} label="mismatch" />}
                        {p.matches_expected == null && p.error && <OkPill ok={false} label="error" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function WebhooksPanel() {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/diagnostics/webhooks?limit=50&status=${status}`, { headers: authHeader() });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load webhooks");
    }
  };
  useEffect(() => { load(); }, [status]);
  const retry = async (delivery_id) => {
    setBusy(delivery_id);
    try {
      await axios.post(`${API}/admin/diagnostics/webhooks/${delivery_id}/retry`, {}, { headers: authHeader() });
      toast.success("Retry succeeded");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed", { duration: 10000 });
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card testid="webhooks-panel-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="text-sm uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Last 50 webhook deliveries</div>
        <div className="flex items-center gap-2">
          <select data-testid="webhook-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="text-xs rounded-full border px-3 py-1" style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)" }}>
            <option value="all">All</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="rejected">Rejected</option>
            <option value="processing">Processing</option>
          </select>
          <button data-testid="webhooks-refresh-btn" onClick={load} className="text-xs underline" style={{ color: "var(--gf-accent)" }}>Refresh</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--gf-text-muted)" }}>No webhook deliveries recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ color: "var(--gf-text-muted)" }}>
                <th className="text-left py-2 pr-4">Received</th>
                <th className="text-left py-2 pr-4">Event type</th>
                <th className="text-left py-2 pr-4">Stripe event id</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Duration</th>
                <th className="text-left py-2 pr-4">Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`webhook-row-${r.id}`} className="border-t" style={{ borderColor: "var(--gf-surface)" }}>
                  <td className="py-2 pr-4 font-mono" style={{ color: "var(--gf-text)" }}>{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="py-2 pr-4">{r.event_type}</td>
                  <td className="py-2 pr-4 font-mono" style={{ color: "var(--gf-text-muted)" }}>{r.stripe_event_id || "—"}</td>
                  <td className="py-2 pr-4"><OkPill ok={r.status === "succeeded"} label={r.status} /></td>
                  <td className="py-2 pr-4">{r.duration_ms != null ? `${r.duration_ms} ms` : "—"}</td>
                  <td className="py-2 pr-4 text-red-700 max-w-[280px] truncate" title={r.error || ""}>{r.error || "—"}</td>
                  <td className="py-2 pr-4">
                    {["failed", "rejected"].includes(r.status) && (
                      <button
                        data-testid={`webhook-retry-${r.id}`}
                        disabled={busy === r.id}
                        onClick={() => retry(r.id)}
                        className="text-xs rounded-full px-3 py-1 border disabled:opacity-50"
                        style={{ borderColor: "var(--gf-surface)", color: "var(--gf-accent)" }}
                      >
                        {busy === r.id ? "Retrying…" : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SubscriptionPanel() {
  const { authHeader } = useAuth();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/diagnostics/subscription/lookup?q=${encodeURIComponent(q.trim())}`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      setData(null);
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };
  const reconcile = async () => {
    if (!data?.business?.id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/businesses/${data.business.id}/subscription/reconcile`, {}, { headers: authHeader() });
      toast.success(r.data.changed ? `Synced · applied ${r.data.applied_fields.join(", ")}` : "Already in sync");
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed", { duration: 12000 });
    } finally { setBusy(false); }
  };
  return (
    <Card testid="subscription-panel-card">
      <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Subscription lookup</div>
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <input
          data-testid="sub-lookup-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Business id, email, cus_..., sub_..., cs_..."
          className="flex-1 min-w-[260px] rounded-full border px-4 py-2 text-sm"
          style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)", color: "var(--gf-text)" }}
        />
        <button data-testid="sub-lookup-btn" disabled={busy} className="rounded-full px-5 py-2 text-sm disabled:opacity-50"
          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Search</button>
      </form>
      {data && (
        <div className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="font-display text-xl" style={{ color: "var(--gf-text)" }} data-testid="sub-result-name">{data.business.business_name || data.business.email}</div>
            <button data-testid="sub-reconcile-btn" disabled={busy} onClick={reconcile}
              className="rounded-full px-4 py-2 text-xs border disabled:opacity-50"
              style={{ borderColor: "var(--gf-surface)", color: "var(--gf-accent)" }}>
              {busy ? "Reconciling…" : "Reconcile subscription"}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-3">
            <div>
              <KV k="Business id" v={data.business.id} testid="sub-biz-id" />
              <KV k="Email" v={data.business.email} />
              <KV k="Account status" v={data.business.account_status} />
              <KV k="Stripe customer id" v={data.business.stripe_customer_id} />
              <KV k="Stripe subscription id" v={data.business.stripe_subscription_id} />
            </div>
            <div>
              <KV k="Local subscription_status" v={data.business.subscription_status} testid="sub-local-status" />
              <KV k="Subscription tier" v={data.business.subscription_tier} />
              <KV k="Entitled (subscription_active)" v={String(data.business.subscription_active)} testid="sub-entitled" />
              <KV k="Current period ends" v={data.business.subscription_expires_at} />
              <KV k="Trial ends" v={data.business.trial_end_at} />
              <KV k="Last reconciled at" v={data.business.subscription_reconciled_at} />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function EmailPanel() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/diagnostics/email`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email status");
    }
  };
  useEffect(() => { load(); }, []);
  const sendTest = async (e) => {
    e.preventDefault();
    if (!to.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostics/email/test`, { to: to.trim() }, { headers: authHeader() });
      r.data.ok ? toast.success(`Test email sent to ${to}`) : toast.error("Send call returned failure — check backend logs");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed", { duration: 10000 });
    } finally { setBusy(false); }
  };
  if (!data) return <Card><p className="text-sm">Loading…</p></Card>;
  return (
    <Card testid="email-panel-card">
      <div className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Email</div>
      <KV k="Provider" v={data.provider} />
      <KV k="Provider configured" v={String(data.provider_configured)} testid="email-provider-ok" />
      <KV k="Sender (transactional)" v={data.sender_transactional} />
      <KV k="Sender (security)" v={data.sender_security} />
      <KV k="Last email sent" v={data.last_sent?.created_at || "—"} testid="email-last-sent" />
      <KV k="Failed emails (24h)" v={String(data.recent_failure_count_24h)} testid="email-failures-24h" />

      <form onSubmit={sendTest} className="mt-5 flex flex-wrap gap-2 items-center">
        <input data-testid="email-test-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 min-w-[240px] rounded-full border px-4 py-2 text-sm"
          style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)", color: "var(--gf-text)" }} />
        <button data-testid="email-send-test-btn" disabled={busy} className="rounded-full px-5 py-2 text-sm disabled:opacity-50"
          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
          {busy ? "Sending…" : "Send test email"}
        </button>
      </form>

      {data.recent_failures_24h?.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Recent failures</div>
          <ul className="space-y-1 text-xs">
            {data.recent_failures_24h.map((f, i) => (
              <li key={i} className="font-mono" style={{ color: "var(--gf-text-soft)" }}>
                {(f.created_at || "").slice(0, 19)} · {f.to} · {(f.error || "").slice(0, 200)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function SystemPanel() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/diagnostics/system`, { headers: authHeader() }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <Card><p className="text-sm">Loading…</p></Card>;
  const hours = Math.floor(data.uptime_seconds / 3600);
  const mins = Math.floor((data.uptime_seconds % 3600) / 60);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Card testid="system-runtime-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Runtime</div>
        <KV k="Environment" v={data.app_env} testid="system-env" />
        <KV k="Deployment version" v={data.app_version} testid="system-version" />
        <KV k="Server started at" v={data.server_started_at} />
        <KV k="Uptime" v={`${hours}h ${mins}m`} testid="system-uptime" />
      </Card>
      <Card testid="system-db-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Database & cache</div>
        <KV k="Database" v={data.database.ok ? "connected" : "unreachable"} testid="system-db-ok" />
        <KV k="Collections" v={data.database.collections} />
        <KV k="Storage size (bytes)" v={data.database.storage_size} />
        <KV k="Cache layer" v={data.cache.enabled ? "enabled" : "not configured"} testid="system-cache" />
      </Card>
      <Card testid="system-jobs-card">
        <div className="text-sm uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Background jobs</div>
        <ul className="text-xs font-mono space-y-1" data-testid="system-jobs-list">
          {(data.background_jobs || []).map((n) => (
            <li key={n} style={{ color: "var(--gf-text-soft)" }}>· {n}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function LogsPanel() {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [level, setLevel] = useState("all");
  const [q, setQ] = useState("");
  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/diagnostics/logs?limit=200&level=${level}&q=${encodeURIComponent(q)}`, { headers: authHeader() });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logs");
    }
  };
  useEffect(() => { load(); }, [level]);
  const colorFor = (lvl) => lvl === "ERROR" ? "#dc2626" : lvl === "WARNING" ? "#d97706" : lvl === "INFO" ? "var(--gf-text-soft)" : "var(--gf-text-muted)";
  return (
    <Card testid="logs-panel-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="text-sm uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Last 200 application logs</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="log-level" value={level} onChange={(e) => setLevel(e.target.value)}
            className="text-xs rounded-full border px-3 py-1"
            style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)" }}>
            <option value="all">All</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <input data-testid="log-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search…" className="text-xs rounded-full border px-3 py-1"
            style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)", color: "var(--gf-text)" }} />
          <button data-testid="log-refresh-btn" onClick={load} className="text-xs underline" style={{ color: "var(--gf-accent)" }}>Refresh</button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto rounded-lg" style={{ background: "var(--gf-surface)" }}>
        <table className="w-full text-[11px] font-mono">
          <tbody>
            {rows.length === 0 && (
              <tr><td className="p-4 text-center" style={{ color: "var(--gf-text-muted)" }}>No log lines match.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: "var(--gf-surface-2)" }}>
                <td className="p-2 align-top whitespace-nowrap" style={{ color: "var(--gf-text-muted)" }}>{(r.ts || "").slice(11, 19)}</td>
                <td className="p-2 align-top whitespace-nowrap" style={{ color: colorFor(r.level) }}>{r.level}</td>
                <td className="p-2 align-top" style={{ color: "var(--gf-text-muted)" }}>{r.logger}</td>
                <td className="p-2 align-top break-all" style={{ color: "var(--gf-text-soft)" }}>{r.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
